import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Mail, Phone, Building2 } from "lucide-react";
import { Button } from "../ui/button";

/**
 * AIResponseModal Component
 */
export function AIResponseModal({ 
  isOpen, 
  onClose, 
  response, 
  isProcessing,
  suggestedCampaign 
}) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        className="fixed bottom-8 left-1/2 -translate-x-1/2 w-full max-w-lg px-4 z-[100]"
      >
        <div className="glass-card rounded-2xl p-6 shadow-2xl border border-primary/20" data-testid="ai-response-modal">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
          
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 pr-6">
              <p className="text-xs text-primary font-medium mb-1">YASNO</p>
              {response ? (
                <p className="text-sm text-foreground whitespace-pre-line">
                  {response}
                  {isProcessing && <span className="inline-block w-1.5 h-4 bg-primary ml-0.5 animate-pulse" />}
                </p>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="flex gap-1">
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                  <span>Thinking...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * VideoModal Component - Shows "How It Works" video with optimized loading
 */
export function VideoModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="glass-card rounded-2xl p-6 max-w-4xl w-full border border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Watch How It Works</h3>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="aspect-video bg-black rounded-xl overflow-hidden relative">
            {/* Loading placeholder */}
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-sm text-muted-foreground">Loading video...</p>
              </div>
            </div>
            <video
              className="w-full h-full object-contain relative z-10"
              controls
              autoPlay
              playsInline
              preload="metadata"
              poster="/logo.png"
              onLoadedData={(e) => {
                // Hide loading placeholder when video is ready
                e.target.style.background = 'black';
              }}
            >
              <source src="/yasno-how-it-works.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          
          <div className="mt-4 flex items-center justify-between">
            <div>
              <p className="font-medium">How YASNO Works</p>
              <p className="text-sm text-muted-foreground">See how to automate your marketing with voice</p>
            </div>
            <Button onClick={onClose}>Close</Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * ContactSalesModal Component
 */
export function ContactSalesModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="glass-card rounded-2xl p-6 max-w-md w-full border border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Contact Sales</h3>
                <p className="text-sm text-muted-foreground">Enterprise Solutions</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-primary/30 transition-colors">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">Email Us</p>
                  <a href="mailto:sales@yasnomktg.com" className="text-sm text-primary hover:underline">
                    sales@yasnomktg.com
                  </a>
                </div>
              </div>
            </div>
            
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-primary/30 transition-colors">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">Call Us</p>
                  <a href="tel:+13025202688" className="text-sm text-primary hover:underline">
                    +1 (302) 520-2688
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <h4 className="font-medium mb-3">Enterprise Features</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>✓ Custom AI model training</li>
              <li>✓ Dedicated account manager</li>
              <li>✓ SLA with 99.99% uptime</li>
              <li>✓ Advanced security & compliance</li>
              <li>✓ White-label options</li>
            </ul>
          </div>
          
          <Button className="w-full mt-6" onClick={onClose}>
            Close
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
